/**
 * StoneDevelopmentGrid - Stone Investment themed Development/Project Grid Component
 * 
 * Grid layout for development cards with Stone Investment's luxury portfolio aesthetic:
 * - Editorial grid layouts
 * - Skeleton loader with Stone styling
 * - Empty state with elegant typography
 */

'use client';

import { Building2 } from 'lucide-react';
import { StoneDevelopmentCard, type DevelopmentCardData } from './StoneDevelopmentCard';

export interface StoneDevelopmentGridProps {
  developments: DevelopmentCardData[];
  basePath: string;
  variant?: 'grid' | 'list' | 'featured';
  columns?: 1 | 2 | 3;
  cardVariant?: 'default' | 'compact' | 'featured' | 'editorial';
  showUnitsProgress?: boolean;
  showTimeline?: boolean;
  showDeveloper?: boolean;
  locale?: string;
  emptyMessage?: string;
  emptyDescription?: string;
  className?: string;
}

export function StoneDevelopmentGrid({
  developments,
  basePath,
  variant = 'grid',
  columns = 2,
  cardVariant = 'default',
  showUnitsProgress = true,
  showTimeline = true,
  showDeveloper = false,
  locale = 'en',
  emptyMessage = 'No developments found',
  emptyDescription = 'Explore our collection of exceptional developments or check back soon for new projects.',
  className = '',
}: StoneDevelopmentGridProps) {
  // Empty state - Stone Investment styled
  if (developments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
        <div className="w-20 h-20 border border-stone-300 flex items-center justify-center mb-8">
          <Building2 size={40} className="text-stone-400" strokeWidth={1} />
        </div>
        <h3 className="font-serif text-3xl text-stone-900 mb-3 italic">{emptyMessage}</h3>
        <p className="text-stone-500 font-sans text-sm tracking-wide max-w-md">{emptyDescription}</p>
      </div>
    );
  }

  // Grid layout classes based on variant
  const gridClasses = variant === 'list' 
    ? 'flex flex-col gap-10'
    : variant === 'featured'
    ? 'grid gap-12 grid-cols-1 lg:grid-cols-2'
    : `grid gap-12 ${
        columns === 1 ? 'grid-cols-1' :
        columns === 3 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' :
        'grid-cols-1 md:grid-cols-2'
      }`;

  // Determine card variant based on grid variant
  const getCardVariant = (index: number) => {
    if (cardVariant !== 'default') return cardVariant;
    if (variant === 'featured' && index === 0) return 'featured';
    if (variant === 'list') return 'compact';
    return 'default';
  };

  return (
    <div className={`${gridClasses} ${className}`}>
      {developments.map((development, index) => (
        <StoneDevelopmentCard
          key={development.id}
          development={development}
          href={`${basePath}/developments/${development.slug || development.id}`}
          variant={getCardVariant(index)}
          showUnitsProgress={showUnitsProgress}
          showTimeline={showTimeline}
          showDeveloper={showDeveloper}
          locale={locale}
        />
      ))}
    </div>
  );
}

/**
 * Loading skeleton for StoneDevelopmentGrid
 */
export function StoneDevelopmentGridSkeleton({
  count = 4,
  columns = 2,
  className = '',
}: {
  count?: number;
  columns?: 1 | 2 | 3;
  className?: string;
}) {
  const gridClasses = `grid gap-12 ${
    columns === 1 ? 'grid-cols-1' :
    columns === 3 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' :
    'grid-cols-1 md:grid-cols-2'
  }`;

  return (
    <div className={`${gridClasses} ${className}`}>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="flex flex-col gap-6 animate-pulse">
          {/* Image skeleton - 4:3 aspect ratio for development cards */}
          <div className="aspect-[4/3] bg-gradient-to-br from-stone-200 to-stone-100 relative">
            {/* Type badge skeleton */}
            <div className="absolute top-6 left-6 h-6 w-24 bg-stone-300/50" />
            {/* Status badge skeleton */}
            <div className="absolute top-6 right-6 h-6 w-20 bg-stone-300/50" />
          </div>
          
          {/* Content skeleton */}
          <div className="flex flex-col gap-4 pt-2">
            {/* Stage badge */}
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-stone-300 rounded-full" />
              <div className="h-3 bg-stone-200 w-24" />
            </div>

            {/* Title */}
            <div className="h-8 bg-stone-200 w-3/4" />
            
            {/* Divider */}
            <div className="border-t border-stone-200" />

            {/* Metadata row */}
            <div className="flex gap-4">
              <div className="h-4 bg-stone-100 w-20" />
              <div className="h-4 bg-stone-100 w-16" />
              <div className="h-4 bg-stone-100 w-24" />
            </div>

            {/* Progress bar */}
            <div className="space-y-2">
              <div className="flex justify-between">
                <div className="h-3 bg-stone-100 w-16" />
                <div className="h-3 bg-stone-100 w-20" />
              </div>
              <div className="h-1 bg-stone-200 w-full" />
            </div>
            
            {/* Price row */}
            <div className="flex justify-between items-end pt-2">
              <div className="h-6 bg-stone-200 w-32" />
              <div className="h-5 w-5 bg-stone-200" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default StoneDevelopmentGrid;
